import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  formResponseSchema,
  gearResponseSchema,
  inventoryUnitResponseSchema,
  type InventoryUnit,
} from '@unit/shared';
import { apiPost } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { ApiError } from '../../components/ApiError.js';
import { availableActions, GearActionPanel, type GearAction } from '../gear/GearActions.js';

/**
 * Actions available on a unit sitting *unheld* in company stock — the gap
 * story 43 closes. A held unit has no actions here at all: those still run
 * through the assignment-based flow in `GearActions.tsx`, reached from the
 * soldier's own gear page.
 */
export type UnitAction = 'issue' | 'damage' | 'repair' | 'writeOff' | 'correct';

export function availableUnitActions(u: InventoryUnit, opts: { storeroom: boolean; admin: boolean }): UnitAction[] {
  if (u.currentHolderUid) return [];
  const out: UnitAction[] = [];
  if (opts.storeroom) {
    if (u.status === 'in_stock') out.push('issue', 'damage');
    if (u.status === 'damaged') out.push('repair');
    if (u.status === 'in_stock' || u.status === 'damaged') out.push('writeOff');
  }
  // Admin-only, and available regardless of status — this is fixing a
  // mistake, not acting on the item's real state.
  if (opts.admin) out.push('correct');
  return out;
}

const input =
  'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const danger = 'rounded-lg bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40';

export function UnitActionPanel({
  unit,
  action,
  onDone,
  onClose,
}: {
  unit: InventoryUnit;
  action: UnitAction;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" data-testid={`unit-panel-${action}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-slate-600">{t(`inventory.${action}Hint`)}</p>
        <button type="button" onClick={onClose} className="text-xs text-slate-500 underline-offset-4 hover:underline">
          {t('gear.close')}
        </button>
      </div>
      {action === 'issue' && <IssueUnitForm unit={unit} />}
      {action === 'damage' && <DamageForm unit={unit} onDone={onDone} />}
      {action === 'repair' && <RepairForm unit={unit} onDone={onDone} />}
      {action === 'writeOff' && <WriteOffUnitForm unit={unit} onDone={onDone} />}
      {action === 'correct' && <CorrectUnitForm unit={unit} onDone={onDone} />}
    </div>
  );
}

/**
 * Fixes a data-entry mistake directly — a mistyped serial, the wrong
 * condition. Deliberately separate from every other form here: this is the
 * one action in the app that edits a field instead of recording an event,
 * which is why it needs a reason and admin only (story 43, the follow-up to
 * "so how do I edit inventory that is serialized?").
 */
function CorrectUnitForm({ unit, onDone }: { unit: InventoryUnit; onDone: () => void }) {
  const { t } = useTranslation();
  const [serialNumber, setSerialNumber] = useState(unit.serialNumber);
  const [condition, setCondition] = useState(unit.condition);
  const [notes, setNotes] = useState(unit.notes ?? '');
  const [reason, setReason] = useState('');
  const act = useAction(() => {
    const body: { serialNumber?: string; condition?: typeof condition; notes?: string | null; reason: string } = {
      reason: reason.trim(),
    };
    if (serialNumber.trim() !== unit.serialNumber) body.serialNumber = serialNumber.trim();
    if (condition !== unit.condition) body.condition = condition;
    const trimmedNotes = notes.trim() || null;
    if (trimmedNotes !== unit.notes) body.notes = trimmedNotes;
    return apiPost(`/inventory/units/${unit.id}/correct`, inventoryUnitResponseSchema, body);
  });
  const changed =
    serialNumber.trim() !== unit.serialNumber ||
    condition !== unit.condition ||
    (notes.trim() || null) !== unit.notes;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act.run()) onDone();
  };
  return (
    <form onSubmit={(e) => void submit(e)}>
      <label className="text-sm text-slate-600">
        {t('inventory.columns.serial')}
        <input
          className={`${input} latin-inline`}
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          data-testid="unit-correct-serial"
        />
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('inventory.columns.condition')}
        <select
          className={input}
          value={condition}
          onChange={(e) => setCondition(e.target.value as typeof condition)}
          data-testid="unit-correct-condition"
        >
          <option value="good">{t('inventory.condition.good')}</option>
          <option value="needs_repair">{t('inventory.condition.needs_repair')}</option>
          <option value="unusable">{t('inventory.condition.unusable')}</option>
        </select>
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('inventory.notes')}
        <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="unit-correct-notes" />
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('inventory.reason')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="unit-correct-reason" />
      </label>
      <button
        type="submit"
        disabled={act.busy || !changed || reason.trim().length < 3}
        className={`mt-3 ${danger}`}
        data-testid="unit-correct-submit"
      >
        {t('inventory.confirmCorrect')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </form>
  );
}

/**
 * Issues this exact unit to a soldier named by personal number — a shortcut
 * around the full wizard for "I'm already looking at this serial, hand it to
 * X" (story 43). On success this goes to the new pending form, same as a
 * transfer does, since that is now the soldier's problem to sign.
 */
function IssueUnitForm({ unit }: { unit: InventoryUnit }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [personalNumber, setPersonalNumber] = useState('');
  const act = useAction(() =>
    apiPost(`/inventory/units/${unit.id}/issue`, formResponseSchema, {
      toPersonalNumber: personalNumber.trim(),
    }),
  );
  return (
    <>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.transferTo')}
        <input
          className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 latin-inline"
          inputMode="numeric"
          value={personalNumber}
          onChange={(e) => setPersonalNumber(e.target.value)}
          data-testid="unit-issue-personal-number"
        />
      </label>
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && navigate(`/forms/${r.form.id}`))}
        disabled={act.busy || personalNumber.trim().length === 0}
        className={`mt-3 ${primary}`}
        data-testid="unit-issue-submit"
      >
        {t('inventory.confirmIssue')}
      </button>
      <div className="mt-2">
        <ActionError error={act.error} />
        {act.error?.code === 'not_found' && (
          <p className="text-sm text-slate-600">{t('gear.transferNoSuchSoldier')}</p>
        )}
      </div>
    </>
  );
}

function DamageForm({ unit, onDone }: { unit: InventoryUnit; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const act = useAction(() =>
    apiPost(`/inventory/units/${unit.id}/damage`, inventoryUnitResponseSchema, { reason: reason.trim() }),
  );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act.run()) onDone();
  };
  return (
    <form onSubmit={(e) => void submit(e)}>
      <label className="text-sm text-slate-600">
        {t('inventory.reason')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="unit-damage-reason" />
      </label>
      <button type="submit" disabled={act.busy || reason.trim().length < 3} className={`mt-3 ${danger}`} data-testid="unit-damage-submit">
        {t('inventory.confirmDamage')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </form>
  );
}

function RepairForm({ unit, onDone }: { unit: InventoryUnit; onDone: () => void }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const act = useAction(() =>
    apiPost(`/inventory/units/${unit.id}/repair`, inventoryUnitResponseSchema, { notes: notes.trim() || null }),
  );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act.run()) onDone();
  };
  return (
    <form onSubmit={(e) => void submit(e)}>
      <label className="text-sm text-slate-600">
        {t('inventory.notes')}
        <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="unit-repair-notes" />
      </label>
      <button type="submit" disabled={act.busy} className={`mt-3 ${primary}`} data-testid="unit-repair-submit">
        {t('inventory.confirmRepair')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </form>
  );
}

/**
 * A held unit's actions, reached from the registry instead of the holder's
 * gear page — same actions, same forms, same endpoints. There is no
 * "fetch one assignment" endpoint, so this reuses the one that already
 * exists (`GET /users/:uid/gear`, the same call `GearCard` makes) and picks
 * out the line matching this unit.
 */
export function HeldUnitActionsArea({
  unit,
  viewerUid,
  storeroom,
  onClose,
  onChanged,
}: {
  unit: InventoryUnit;
  viewerUid: string | null;
  storeroom: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [action, setAction] = useState<GearAction | null>(null);
  const holderUid = unit.currentHolderUid;
  const state = useApi(holderUid ? `/users/${holderUid}/gear` : null, gearResponseSchema);

  if (!holderUid) return null;
  if (state.kind === 'loading') return <p className="mt-2 text-sm text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const assignment = state.data.assignments.find((a) => a.inventoryUnitId === unit.id);
  if (!assignment) {
    // The unit moved on since the registry loaded — a return or a rotation
    // landed between the list rendering and this fetch resolving.
    return (
      <p className="mt-2 text-sm text-slate-500" data-testid="held-unit-stale">
        {t('inventory.unitChanged')}
      </p>
    );
  }

  const viewer = { isOwner: viewerUid === holderUid, canManage: storeroom, storeroom };
  const actions = availableActions(assignment, viewer);

  return (
    <div className="mt-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" data-testid="held-unit-actions">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-slate-600">{t('inventory.heldUnitHint')}</p>
        <button type="button" onClick={onClose} className="text-xs text-slate-500 underline-offset-4 hover:underline">
          {t('gear.close')}
        </button>
      </div>
      {actions.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">{t('gear.empty')}</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {actions.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAction(action === a ? null : a)}
              className={`rounded px-2 py-0.5 text-xs ring-1 transition ${
                a === 'writeOff' || a === 'report'
                  ? 'text-red-800 ring-red-200 hover:bg-red-50'
                  : 'text-slate-700 ring-slate-300 hover:bg-slate-100'
              }`}
              data-testid={`held-unit-action-${a}`}
            >
              {t(`gear.${a}`)}
            </button>
          ))}
        </div>
      )}
      {action && (
        <GearActionPanel
          assignment={assignment}
          action={action}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            state.reload();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function WriteOffUnitForm({ unit, onDone }: { unit: InventoryUnit; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const act = useAction(() =>
    apiPost(`/inventory/units/${unit.id}/write-off`, inventoryUnitResponseSchema, { reason: reason.trim() }),
  );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act.run()) onDone();
  };
  return (
    <form onSubmit={(e) => void submit(e)}>
      <label className="text-sm text-slate-600">
        {t('inventory.reason')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="unit-write-off-reason" />
      </label>
      <button type="submit" disabled={act.busy || reason.trim().length < 3} className={`mt-3 ${danger}`} data-testid="unit-write-off-submit">
        {t('inventory.confirmWriteOffUnit')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </form>
  );
}
